/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { SelectionMode } from '@fluentui/react/lib/DetailsList';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Selection, SelectionZone } from '@fluentui/react/lib/Selection';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITheme } from '@fluentui/react/lib/Styling';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Paths } from '../../../common/enums/Paths';
import { TeamsInitializationStatus } from '../../../hosts/IHost';
import { EntryPoint, LogCategory, ScenarioName, StatusName, StepName } from '../../../logger';
import { IconName } from '../../../resources/icons/Icons';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { MobileNavigationBar } from '../../MobileNavigationBar/MobileNavigationBar';
import { getMenuEntryStyles } from './MobileCreateRequestMenu.styles';
import { MobileCreateRequestMenuEntry, MobileCreateRequestMenuEntryProps } from './MobileCreateRequestMenuEntry';

export interface CreateRequestMenuProps {
    showNaviBar?: boolean;
}

export const MobileCreateRequestMenu: React.FC<CreateRequestMenuProps> = (
    props: CreateRequestMenuProps
): JSX.Element => {
    const { showNaviBar } = props;
    const loc = getAppHost().getLocale();
    const isPersonalApp = showNaviBar;
    const entryPoint = isPersonalApp ? EntryPoint.PersonalApp : EntryPoint.ComposeExtension;

    const theme: ITheme = useContext(CustomizerContext).customizations.settings.theme;
    const history = useHistory();

    const navigateToCreateBasicApproval = (): void => {
        history.push({ pathname: Paths.BasePath + Paths.CreateApproval });
    };

    const navigateToCreateTemplateApproval = (): void => {
        history.push(Paths.BasePath + Paths.ChooseTemplate, {
            entryPoint: isPersonalApp ? EntryPoint.PersonalApp : EntryPoint.ComposeExtension,
        });
    };

    useEffect((): any => {
        const logger = getAppHost().getLogger();

        getAppHost().setNavBarMenu([], null);

        logger.logTraceInfo(LogCategory.AppBoot, 'Notifying Teams for init success in mobile create request menu');
        getAppHost().notifyTeamsInitialization(TeamsInitializationStatus.Success);

        // mark create request menu v1 scenario.
        logger.markScenario(ScenarioName.CreateRequestMenu, StepName.MobileCreateRequestMenuRendered, StatusName.NA);

        if (entryPoint === EntryPoint.ComposeExtension) {
            logger.markScenario(ScenarioName.AppBoot, StepName.AppBootMobileCreateRequestMenuRendered, StatusName.NA);
            logger.stopScenario(ScenarioName.AppBoot);
        }
        return (): void => {
            // stop create request menu scenario
            logger.stopScenario(ScenarioName.CreateRequestMenu);
        };
    }, [showNaviBar]);

    const menuEntries: MobileCreateRequestMenuEntryProps[] = [
        {
            iconName: IconName.textBulletListSquare,
            text: loc.strings.request_type_basic,
            onClick: navigateToCreateBasicApproval,
        },
        {
            iconName: IconName.notebook,
            text: loc.strings.templates,
            onClick: navigateToCreateTemplateApproval,
        },
    ];

    const selection = useMemo((): any => new Selection({ selectionMode: SelectionMode.none }), []);

    // use FocusZone + SelectionZone because the touch styles of list in SelectionZone works correct on both IOS and Android
    // the style of Fluent UI button or css selectors like hover don't works correct on ios
    return (
        <>
            {showNaviBar && <MobileNavigationBar onSelected={history.goBack} />}
            <FocusZone role='menu'>
                <SelectionZone selection={selection}>
                    {menuEntries.map(
                        (menuEntryProps: MobileCreateRequestMenuEntryProps, index: number): JSX.Element => {
                            return (
                                <Stack
                                    key={index}
                                    aria-posinset={index + 1}
                                    aria-setsize={menuEntries.length}
                                    data-is-focusable
                                    role='menuitem'
                                    styles={getMenuEntryStyles(theme)}
                                    onClick={menuEntryProps.onClick}
                                    onKeyDown={(ev: any): void => {
                                        // not use button because the style of Fluent UI button have issue on IOS.
                                        if (menuEntryProps.onClick && (ev.key === 'Enter' || ev.key === ' ')) {
                                            menuEntryProps.onClick();
                                        }
                                    }}>
                                    <MobileCreateRequestMenuEntry {...menuEntryProps} />
                                </Stack>
                            );
                        }
                    )}
                </SelectionZone>
            </FocusZone>
        </>
    );
};
